import { FC, useRef } from "react";

import { useQuery } from "@tanstack/react-query";

import FeatureFlagStoreContext from "./store";

import getFeatureFlags from "lib/featureFlagsApi";
import LoadingScreen from "components/LoadingScreen";

// Public endpoint, org-level flags: { "<flag_key>": true | false }.
type FeatureFlagsResponse = Record<string, boolean>;

const FeatureFlagManager: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const isFeatureFlagsFetched = useRef(false);
  const {
    isLoading: isLoadingFeatureFlags,
    isFetching: isFetchingFeatureFlags,
  } = useQuery({
    queryKey: ["get-feature-flags"],
    queryFn: () => getFeatureFlags(),
    // Public endpoint: load on every page, logged out and login page included, so
    // flag-driven behaviour (e.g. per-event theming) is correct before login too.
    enabled: !isFeatureFlagsFetched.current,
    retryDelay: 1000,
    refetchOnMount: false,
    onSuccess: (response) => {
      isFeatureFlagsFetched.current = true;
      FeatureFlagStoreContext.setState({
        flags: new Set(
          Object.entries((response.data ?? {}) as FeatureFlagsResponse)
            .filter(([, enabled]) => enabled === true)
            .map(([key]) => key),
        ),
      });
    },
    onError: (error) => {
      console.error("Failed to fetch feature flags", error);
    },
    useErrorBoundary: false,
    retry: (failureCount: number) => failureCount <= 3,
  });
  if (isLoadingFeatureFlags && isFetchingFeatureFlags) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

export default FeatureFlagManager;
